import { FORMAT_DEFAULT_SIZE, isCountedFormat } from './bottleFormat.js'

export const BOTTLE_ACQUISITION_OPTIONS = Object.freeze([
  Object.freeze({ value: '', label: '— Skip —' }),
  Object.freeze({ value: 'purchase', label: 'Retailer purchase' }),
  Object.freeze({ value: 'blind_buy', label: 'Blind buy' }),
  Object.freeze({ value: 'tester', label: 'Tester' }),
  Object.freeze({ value: 'gifted', label: 'Gift' }),
  Object.freeze({ value: 'decant', label: 'Decant' }),
  Object.freeze({ value: 'swap', label: 'Swap' }),
  Object.freeze({ value: 'other', label: 'Other' }),
])

// Matches fragrance.bottle_inventory_bottle_format_check (FOS-SAMPLE-TRAVEL-001).
const VALID_BOTTLE_FORMATS = new Set(['full', 'travel', 'sample'])
export const MAX_BOTTLES_PER_ADD = 100

// The Add a Bottle sheet's kind picker. Samples and travel sprays (FOS-SAMPLE-TRAVEL-001) get
// their own small sizes — 1.5 ml is the usual sample — and a count; bottle_inventory.size_ml is
// numeric(…,1) in production, so those decimals never depend on the bottle_size_decimal flag.
// Leaving Sample/Travel keeps the size only when the standard grid offers it: a hidden 1.5 ml
// would otherwise fail the whole-number parser on save with no chip shown as selected.
export function chooseAddBottleFormat(current, next, standardSizes = []) {
  const format = next === current.bottle_format ? null : next
  if (!isCountedFormat(format)) {
    const keep = standardSizes.some(size => String(size) === String(current.size_ml))
    return { ...current, bottle_format: format, count: 1, size_ml: keep ? current.size_ml : '', size_other: false }
  }
  const size = Number(current.size_ml)
  const keep = size > 0 && (format === 'sample' ? size <= 5 : size >= 4 && size <= 30)
  return { ...current, bottle_format: format, size_ml: keep ? current.size_ml : String(FORMAT_DEFAULT_SIZE[format]), size_other: false }
}

const VALID_ACQUISITION_TYPES = new Set(
  BOTTLE_ACQUISITION_OPTIONS.map(option => option.value).filter(Boolean),
)

const trimmedOrNull = value => typeof value === 'string' ? value.trim() || null : value ?? null

export function parseBottleSize(value, { supportsDecimalSizes = false } = {}) {
  const raw = String(value ?? '').trim()
  if (!raw) throw new Error('Bottle size is required.')

  const size = Number(raw)
  if (!Number.isFinite(size) || size <= 0 || size > 1000) {
    throw new Error('Bottle size must be between 0 and 1000 ml.')
  }
  if (!supportsDecimalSizes && !Number.isInteger(size)) {
    throw new Error('Bottle size must be a whole number until decimal sizes are enabled.')
  }
  return size
}

export function bottleInventoryInsertPayload({
  userId,
  fragranceId,
  details = {},
  acquiredOn = new Date().toISOString().slice(0, 10),
}) {
  const acquisitionType = details.acquisition_type ?? details.purchase_kind ?? null
  if (acquisitionType && !VALID_ACQUISITION_TYPES.has(acquisitionType)) {
    throw new Error('Choose a valid acquisition type.')
  }

  const bottleFormat = details.bottle_format ?? null
  if (bottleFormat && !VALID_BOTTLE_FORMATS.has(bottleFormat)) {
    throw new Error('Choose Full bottle, Travel or Sample.')
  }

  return {
    user_id: userId,
    fragrance_id: fragranceId,
    is_backup: true,
    size_ml: details.bottle_ml ?? null,
    acquisition_source: trimmedOrNull(details.acquisition_source ?? details.source),
    acquisition_type: acquisitionType,
    acquired_from: trimmedOrNull(details.acquired_from ?? details.gifted_from),
    acquired_on: details.acquired_on ?? acquiredOn,
    status: details.status || 'sealed',
    ...(bottleFormat ? { bottle_format: bottleFormat } : {}),
    // Only sent when a non-default form was chosen: the column arrives with the
    // 20260930_bottle_product_forms migration and defaults to 'perfume', so a
    // plain perfume add must not name it (it would fail before that migration).
    ...(details.product_form && details.product_form !== 'perfume' ? { product_form: details.product_form } : {}),
  }
}

// One row per physical bottle: adding a pack of 3 samples inserts 3 rows, so the card shows
// "Sample · 1.5 ml ×3" and lowering the count later marks single vials as given away.
export function bottleInventoryInsertRows({ count = 1, ...options }) {
  const total = Math.round(Number(count))
  if (!Number.isFinite(total) || total < 1 || total > MAX_BOTTLES_PER_ADD) {
    throw new Error(`How many must be between 1 and ${MAX_BOTTLES_PER_ADD}.`)
  }
  const row = bottleInventoryInsertPayload(options)
  return Array.from({ length: total }, () => ({ ...row }))
}

export function indexBottleInventory(rows = []) {
  const byFragrance = new Map()
  for (const row of rows) {
    if (!row?.fragrance_id) continue
    const current = byFragrance.get(row.fragrance_id) || []
    current.push(row)
    byFragrance.set(row.fragrance_id, current)
  }
  return byFragrance
}

/**
 * Extract unique product forms owned for a fragrance
 * @param {Array} inventoryRows - bottle_inventory rows for this fragrance
 * @returns {Array<string>} - unique product_form values (e.g., ['perfume', 'body_wash'])
 */
export function ownedProductForms(inventoryRows = []) {
  const forms = new Set()
  for (const row of inventoryRows) {
    if (row?.product_form) forms.add(row.product_form)
  }
  return Array.from(forms).sort()
}

export function bottleSizesForFragrance(fragrance, inventoryRows = []) {
  if (inventoryRows.length > 0) {
    return inventoryRows.map(row => ({
      ml: row.size_ml,
      id: row.id,
      concentration: fragrance.concentration || '',
      isSample: (fragrance.concentration || '').toLowerCase() === 'sample',
      status: row.status || null,
      finished_at: row.finished_at || null,
    }))
  }

  const concentration = fragrance.concentration || ''
  const isSample = concentration.toLowerCase() === 'sample'
  if (fragrance.bottle_ml == null && !isSample) return []
  return [{
    ml: fragrance.bottle_ml,
    id: fragrance.id,
    concentration,
    isSample,
    status: fragrance.finished_at ? 'finished' : null,
    finished_at: fragrance.finished_at || null,
  }]
}
